// /home/labdoo/zitech-nexus/frontend/src/pages/admin/AdminSupportTicketDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supportAPI } from '../../services/api';
import './AdminSupportManagement.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  User: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Email: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Calendar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Message: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Send: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ast-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Update: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 4v6h-6" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  Reply: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <polyline points="10 11 14 15 10 19" />
    </svg>
  ),
  Notes: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
};

interface Reply {
  id: number;
  message: string;
  is_admin_reply: boolean;
  admin_name: string;
  created_at: string;
}

interface Ticket {
  id: number;
  ticket_number: string;
  name: string;
  email: string;
  subject_display: string;
  message: string;
  status: string;
  status_display: string;
  priority: string;
  priority_display: string;
  admin_notes: string;
  created_at: string;
  updated_at: string;
  replies: Reply[];
}

const AdminSupportTicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [status, setStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchTicket();
  }, [id]);

  const fetchTicket = async () => {
    try {
      const response = await supportAPI.adminGetTicketDetail(parseInt(id!));
      setTicket(response.data);
      setStatus(response.data.status);
      setAdminNotes(response.data.admin_notes || '');
    } catch (error) {
      console.error('Error fetching ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTicket = async () => {
    setUpdating(true);
    try {
      await supportAPI.adminUpdateTicketStatus(parseInt(id!), status);
      alert('Ticket updated successfully');
      fetchTicket();
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('Failed to update ticket');
    } finally {
      setUpdating(false);
    }
  };

  const sendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyMessage.trim()) {
      alert('Please enter a reply message');
      return;
    }

    setSendingReply(true);
    try {
      await supportAPI.addReply(parseInt(id!), replyMessage);
      setReplyMessage('');
      fetchTicket();
      alert('Reply sent successfully');
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'open': return '#f59e0b';
      case 'in_progress': return '#0ea5e9';
      case 'resolved': return '#10b981';
      case 'closed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusBackground = (status: string) => {
    switch(status) {
      case 'open': return '#fef3c7';
      case 'in_progress': return '#dbeafe';
      case 'resolved': return '#d1fae5';
      case 'closed': return '#f3f4f6';
      default: return '#f3f4f6';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'open': return 'Open';
      case 'in_progress': return 'In Progress';
      case 'resolved': return 'Resolved';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#0ea5e9';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPriorityBackground = (priority: string) => {
    switch(priority) {
      case 'urgent': return '#fee2e2';
      case 'high': return '#fef3c7';
      case 'medium': return '#dbeafe';
      case 'low': return '#d1fae5';
      default: return '#f3f4f6';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="ast-loading-container">
        <div className="ast-spinner">
          <Icons.Spinner />
        </div>
        <p className="ast-loading-text">Loading ticket details...</p>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ast-error-container">
        <div className="ast-error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 className="ast-error-title">Ticket Not Found</h2>
        <p className="ast-error-text">The ticket you're looking for doesn't exist or has been removed.</p>
        <button className="ast-error-btn" onClick={() => navigate('/dashboard/admin/support/tickets')}>
          <Icons.Back />
          Back to Tickets
        </button>
      </div>
    );
  }

  return (
    <div className="ast-detail-container">
      {/* Header */}
      <div className="ast-detail-header">
        <div className="ast-detail-header-left">
          <button className="ast-back-btn" onClick={() => navigate('/dashboard/admin/support/tickets')}>
            <Icons.Back />
            Back to Tickets
          </button>
          <h1 className="ast-detail-title">Ticket #{ticket.ticket_number}</h1>
          <div className="ast-detail-meta">
            <span className="ast-meta-item">
              <Icons.User />
              {ticket.name}
            </span>
            <span className="ast-meta-divider">|</span>
            <span className="ast-meta-item">
              <Icons.Email />
              {ticket.email}
            </span>
            <span className="ast-meta-divider">|</span>
            <span className="ast-meta-item">
              <Icons.Calendar />
              {new Date(ticket.created_at).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
          </div>
        </div>
        <div className="ast-detail-header-status">
          <span 
            className="ast-status-badge-large"
            style={{
              background: getStatusBackground(ticket.status),
              color: getStatusColor(ticket.status)
            }}
          >
            <Icons.StatusDot />
            {getStatusLabel(ticket.status)}
          </span>
        </div>
      </div>

      {/* Detail Grid */}
      <div className="ast-detail-grid">
        {/* Left Column */}
        <div className="ast-detail-left">
          {/* Message */}
          <div className="ast-card">
            <h3 className="ast-card-title">
              <Icons.Message />
              Message
            </h3>
            <div className="ast-message-text">
              <p>{ticket.message}</p>
            </div>
          </div>

          {/* Conversation */}
          <div className="ast-card">
            <h3 className="ast-card-title">
              <Icons.Reply />
              Conversation
            </h3>
            <div className="ast-replies-list">
              {ticket.replies && ticket.replies.length > 0 ? (
                ticket.replies.map((reply) => (
                  <div key={reply.id} className={`ast-reply-item ${reply.is_admin_reply ? 'ast-reply-admin' : 'ast-reply-user'}`}>
                    <div className="ast-reply-header">
                      <strong>{reply.is_admin_reply ? reply.admin_name || 'Support Team' : ticket.name}</strong>
                      <span className="ast-reply-date">{formatDate(reply.created_at)}</span>
                    </div>
                    <div className="ast-reply-content">{reply.message}</div>
                  </div>
                ))
              ) : (
                <p className="ast-no-replies">No replies yet. Send a reply below.</p>
              )}
            </div>

            {/* Reply Form */}
            <div className="ast-reply-form">
              <h4>Send Reply</h4>
              <form onSubmit={sendReply}>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={4}
                  placeholder="Type your reply here..."
                  required
                />
                <button type="submit" className="ast-send-reply-btn" disabled={sendingReply}>
                  {sendingReply ? (
                    <>
                      <span className="ast-spinner-small"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Icons.Send />
                      Send Reply
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="ast-detail-right">
          {/* Status & Priority */}
          <div className="ast-card">
            <h3 className="ast-card-title">
              <Icons.Update />
              Status & Priority
            </h3>
            <div className="ast-form-group">
              <label className="ast-form-label">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="ast-status-select"
                style={{
                  background: getStatusBackground(status),
                  color: getStatusColor(status),
                  borderColor: getStatusColor(status)
                }}
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div className="ast-form-group">
              <label className="ast-form-label">Priority</label>
              <div className="ast-priority-display">
                <span 
                  className="ast-priority-badge-large"
                  style={{
                    background: getPriorityBackground(ticket.priority),
                    color: getPriorityColor(ticket.priority)
                  }}
                >
                  <span className="ast-priority-dot" style={{ background: getPriorityColor(ticket.priority) }}></span>
                  {ticket.priority_display}
                </span>
              </div>
            </div>

            <button className="ast-update-btn" onClick={updateTicket} disabled={updating}>
              {updating ? (
                <>
                  <span className="ast-spinner-small"></span>
                  Updating...
                </>
              ) : (
                <>
                  <Icons.Update />
                  Update Ticket
                </>
              )}
            </button>
          </div>

          {/* Admin Notes */}
          <div className="ast-card">
            <h3 className="ast-card-title">
              <Icons.Notes />
              Admin Notes (Internal)
            </h3>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={4}
              placeholder="Add internal notes about this ticket..."
              className="ast-admin-notes"
            />
            <button className="ast-save-notes-btn" onClick={updateTicket} disabled={updating}>
              <Icons.Update />
              Save Notes
            </button>
          </div>

          {/* Ticket Info */}
          <div className="ast-card">
            <h3 className="ast-card-title">Ticket Info</h3>
            <div className="ast-info-row">
              <span className="ast-info-label">Created</span>
              <span className="ast-info-value">{formatDate(ticket.created_at)}</span>
            </div>
            <div className="ast-info-row">
              <span className="ast-info-label">Updated</span>
              <span className="ast-info-value">{formatDate(ticket.updated_at)}</span>
            </div>
            <div className="ast-info-row">
              <span className="ast-info-label">Subject</span>
              <span className="ast-info-value">{ticket.subject_display}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSupportTicketDetail;